import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAdmin } from "@/lib/superadmin";

interface NavItem {
  label: string;
  path: string;
  icon: string;
  badge?: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    title: "Overview",
    items: [
      { label: "Dashboard", path: "/superadmin/dashboard", icon: "ri-dashboard-3-line" },
      { label: "Analytics & BI", path: "/superadmin/analytics", icon: "ri-line-chart-line" },
    ],
  },
  {
    title: "Operations",
    items: [
      { label: "Tenants", path: "/superadmin/tenants", icon: "ri-store-2-line" },
      { label: "Menu Oversight", path: "/superadmin/menu", icon: "ri-restaurant-line" },
      { label: "Subscriptions", path: "/superadmin/billing", icon: "ri-bank-card-line" },
      { label: "Financial Ops", path: "/superadmin/finance", icon: "ri-money-rupee-circle-line" },
      { label: "Voided Bills", path: "/superadmin/voided", icon: "ri-delete-bin-6-line" },
    ],
  },
  {
    title: "Platform",
    items: [
      { label: "Plans & Pricing", path: "/superadmin/plans", icon: "ri-price-tag-3-line" },
      { label: "Feature Control", path: "/superadmin/entitlements", icon: "ri-toggle-line" },
      { label: "Coupons & Promos", path: "/superadmin/coupons", icon: "ri-coupon-3-line" },
      { label: "Announcements", path: "/superadmin/announcements", icon: "ri-megaphone-line" },
    ],
  },
  {
    title: "Administration",
    items: [
      { label: "System & Security", path: "/superadmin/system", icon: "ri-shield-check-line" },
    ],
  },
];

export default function Sidebar({
  open,
  onClose,
  collapsed,
  onToggleCollapse,
}: {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const location = useLocation();
  const { me, signOut } = useAdmin();
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const displayName = me.name || me.email;
  const initials =
    (me.name || me.email)
      .split(/[@\s.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "AD";

  const toggleGroup = (title: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-foreground-950/40 backdrop-blur-[2px] lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed top-0 left-0 z-50 h-full bg-background-50 border-r border-background-200/80 flex flex-col transition-all duration-300 lg:translate-x-0 lg:static lg:z-auto ${
          collapsed ? "w-[68px]" : "w-64"
        } ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        {/* Brand */}
        <div
          className={`flex items-center h-16 border-b border-background-200/80 shrink-0 ${
            collapsed ? "justify-center px-2" : "gap-2.5 px-5"
          }`}
        >
          <img
            src="/images/logo.png"
            alt="EzyDinePro"
            className={collapsed ? "h-7 w-auto" : "h-8 w-auto"}
          />
          {!collapsed && (
            <span className="px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-600 text-[9px] font-label font-bold uppercase tracking-wider">
              Super Admin
            </span>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
          {navGroups.map((group) => (
            <div key={group.title}>
              {!collapsed ? (
                <button
                  onClick={() => toggleGroup(group.title)}
                  className="flex items-center justify-between w-full px-2 mb-1.5 cursor-pointer group"
                >
                  <span className="text-[10px] font-label font-semibold text-foreground-400 uppercase tracking-widest">
                    {group.title}
                  </span>
                  <i
                    className={`text-xs text-foreground-400 opacity-0 group-hover:opacity-100 transition-all ${
                      collapsedGroups[group.title] ? "-rotate-90" : ""
                    } ri-arrow-down-s-line`}
                  ></i>
                </button>
              ) : (
                <div className="h-px bg-background-200/70 mx-2 mb-2 first:hidden"></div>
              )}

              {(collapsed || !collapsedGroups[group.title]) && (
                <ul className="space-y-0.5">
                  {group.items.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <li key={item.path} className="relative">
                        <Link
                          to={item.path}
                          onClick={onClose}
                          title={collapsed ? item.label : undefined}
                          className={`relative flex items-center rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                            collapsed ? "justify-center py-2.5" : "gap-3 px-3 py-2.5"
                          } ${
                            active
                              ? "bg-primary-50 text-primary-700"
                              : "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                          }`}
                        >
                          {active && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-primary-500"></span>
                          )}
                          <span className="w-5 h-5 flex items-center justify-center">
                            <i
                              className={`${item.icon} text-base ${
                                active ? "text-primary-600" : ""
                              }`}
                            ></i>
                          </span>
                          {!collapsed && <span className="flex-1">{item.label}</span>}
                          {!collapsed && item.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-accent-500 text-background-50">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </nav>

        {/* Collapse toggle (desktop) */}
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex items-center justify-center h-9 mx-3 mb-2 rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <i
            className={`ri-arrow-left-double-line text-lg transition-transform duration-300 ${
              collapsed ? "rotate-180" : ""
            }`}
          ></i>
        </button>

        {/* Profile footer */}
        <div
          className={`border-t border-background-200/80 shrink-0 ${
            collapsed ? "px-2 py-3" : "px-4 py-4"
          }`}
        >
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
            <div
              className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shrink-0 ring-2 ring-primary-100"
              title={displayName}
            >
              <span className="text-xs font-bold text-background-50">{initials}</span>
            </div>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-900 truncate">
                    {displayName}
                  </p>
                  <p className="text-[11px] text-foreground-400 truncate">Platform Owner</p>
                </div>
                <button
                  onClick={() => void signOut()}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
                  title="Sign out"
                >
                  <i className="ri-logout-box-r-line text-sm"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
